/**
 * Registro delle voci di cronologia percorse DENTRO l'app, in questa scheda.
 *
 * Serve a una domanda che la History API non sa rispondere: "la voce prima di
 * questa è una pagina dell'app, e quale?". `history.back()` va sempre alla voce
 * precedente, anche se è un altro sito o se l'utente è arrivato qui da un link
 * incollato; il breadcrumb "Dispositivi" che facesse così porterebbe fuori
 * dall'app. Con questo registro si torna indietro solo quando la voce
 * precedente è quella che l'etichetta promette, altrimenti si naviga alla rotta
 * padre.
 *
 * Le voci sono indicizzate dalla `key` che React Router dà a ogni voce di
 * cronologia: sopravvive al ricaricamento (sta in `history.state`), quindi
 * dopo un F5 il registro, salvato in sessionStorage, si ritrova al suo posto.
 *
 * Nel dubbio il registro dimentica invece di indovinare: una voce sconosciuta
 * riparte da zero, e il peggio che può succedere è navigare alla rotta padre
 * invece di tornare indietro — mai il contrario.
 */
import type { Location, NavigationType } from "react-router-dom";

export interface VoceCronologia {
  key: string;
  pathname: string;
  search: string;
}

interface Registro {
  voci: VoceCronologia[];
  pos: number;
}

const CHIAVE = "winkonsupport_cronologia";
/** Oltre, le voci più vecchie si scartano: servono solo quelle vicine. */
const MASSIMO_VOCI = 100;

function leggi(): Registro {
  try {
    const grezzo = sessionStorage.getItem(CHIAVE);
    if (!grezzo) return { voci: [], pos: -1 };
    const r = JSON.parse(grezzo) as Registro;
    if (!Array.isArray(r.voci) || typeof r.pos !== "number") return { voci: [], pos: -1 };
    return r;
  } catch {
    return { voci: [], pos: -1 };
  }
}

let registro: Registro = leggi();

function salva() {
  try {
    sessionStorage.setItem(CHIAVE, JSON.stringify(registro));
  } catch {
    // Storage negato o pieno: il registro resta valido in memoria fino al
    // prossimo ricaricamento, poi si riparte dalla rotta padre.
  }
}

/**
 * Da chiamare a ogni cambio di location. Idempotente sulla stessa `key`: lo
 * StrictMode di sviluppo esegue gli effetti due volte, e un PUSH registrato due
 * volte inventerebbe una voce che non c'è.
 */
export function registraNavigazione(location: Location, tipo: NavigationType) {
  const voce: VoceCronologia = { key: location.key, pathname: location.pathname, search: location.search };
  const corrente = registro.voci[registro.pos];

  if (corrente?.key === voce.key) {
    registro.voci[registro.pos] = voce;
  } else if (tipo === "PUSH") {
    registro.voci = [...registro.voci.slice(0, registro.pos + 1), voce].slice(-MASSIMO_VOCI);
    registro.pos = registro.voci.length - 1;
  } else if (tipo === "REPLACE" && registro.pos >= 0) {
    registro.voci[registro.pos] = voce;
  } else {
    // POP (Indietro/Avanti, ricaricamento, primo caricamento).
    const idx = registro.voci.findIndex((v) => v.key === voce.key);
    if (idx >= 0) {
      registro.pos = idx;
      registro.voci[idx] = voce;
    } else {
      registro = { voci: [voce], pos: 0 };
    }
  }
  salva();
}

/** La voce subito prima di quella corrente, se è una pagina dell'app. */
export function vocePrecedente(): VoceCronologia | null {
  return registro.pos > 0 ? registro.voci[registro.pos - 1] ?? null : null;
}
